import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useGovernmentHeaderStore } from 'store/useGovernmentHeaderStore';
import { Body3Medium, Body4Medium, Body5Medium, Body5Regular, Caption1Regular } from 'styles/Global/typography';
import { BackgroundColor, BorderColor, PrimaryColor, TextColor } from 'styles/Global/colorSemantic';
import { useLocation, useBlocker } from 'react-router-dom';
import IcWarningFill from 'assets/Icons/Warning/ToolTipDisabled';
import IcOpenAi from 'assets/Icons/Ai/IcOpenAi';
import { ColumnDivider } from 'styles/Global/commonStyles';
import IcLoadingDots from 'assets/Icons/Dots/IcLoadingDots';
import IcArrowFillRoundRightBlue from 'assets/Icons/Arrow/IcArrowFillRoundRightBlue';
import IcArrowFillRoundRightGray from 'assets/Icons/Arrow/IcArrowFillRoundRightGray';
import _ from 'lodash';
import MobileLeaveModal from 'components/Modal/MobileLeaveModal';
import useToggle from 'hooks/CustomHooks/useToggle';
import { MODAL_CONTENT, MESSAGES, AI_CONSTANTS } from './constants';
import { useChatMessages } from 'hooks/CustomHooks/useChatMessages';
import { classifyIntent, type Intent } from 'constants/openAiPrompts';
import { useOpenAI } from 'hooks/CustomHooks/useOpenAI';
import { ChatMessage, ChatViewProps } from './types';
import { mindUpDiagnosisApi } from 'api/MindUp/mindUpDiagnosisApi';
import { AiChatUpdateRqData, ServerChatMessage } from 'models/MindUp/MindUpTypes';
import IcCheckRoundFill from 'assets/Icons/Check/ICCheckRoundFill';

const isQuotaExcludedIntent = (intent: Intent) => intent === 'offtopic';

function formatDate(ts: number) {
    const d = new Date(ts);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}/${m}/${day}`;
}

/** chatTimestamp 기준 중복 제거 (순서 보존) */
function dedupByTimestamp<T extends { chatTimestamp?: number }>(arr: T[]): T[] {
    const seen = new Set<number>();
    const out: T[] = [];
    for (const m of arr) {
        if (typeof m.chatTimestamp !== 'number') continue; // chatTimestamp 없으면 스킵
        if (seen.has(m.chatTimestamp)) continue;
        seen.add(m.chatTimestamp);
        out.push(m);
    }
    return out;
}

export default function AskToAiPage({ placeholder = MESSAGES.PLACEHOLDER_DEFAULT, onSendMessage }: ChatViewProps = {}) {
    const { state } = useLocation() as { state?: any };

    // ---------- local state / refs ----------
    const [input, setInput] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [isFocused, setIsFocused] = useState(false);
    const scrollerRef = useRef<HTMLDivElement>(null);
    const bottomSentinelRef = useRef<HTMLDivElement>(null);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isShowHelpModal, setIsShowHelpModal] = useToggle();
    const { setHeaderText } = useGovernmentHeaderStore();

    // ---------- safe state access ----------
    const diagnosis = state?.data?.diagnosis ?? null;
    const token = state?.data?.token ?? '';
    const diagnosisInfo = state?.data?.diagnosisInfo ?? null;
    const voucher = state?.data?.voucher ?? null;
    const analysisResult = state?.data?.analysisResult ?? state?.data?.diagnosis ?? '';
    const testRawData = state?.testRawData ?? '';

    // ---------- chatId ----------
    const rawId = (diagnosisInfo && diagnosisInfo._id) || (diagnosis && diagnosis._id) || null;
    const hasChatId = !!rawId;
    const chatId = hasChatId ? String(rawId) : 'pending-chat-id';

    // ---------- 서버 이력 -> ChatMessage[] (chatTimestamp 없는 항목은 버림) ----------
    const serverList: ServerChatMessage[] =
        (diagnosis?.assessmentAiResult?.chatMessageList as ServerChatMessage[] | undefined) ?? (diagnosisInfo?.assessmentAiResult?.chatMessageList as ServerChatMessage[] | undefined) ?? [];

    const mappedFromServer: ChatMessage[] = hasChatId
        ? serverList
              .filter((m) => typeof m?.chatTimestamp === 'number') // ← 핵심: chatTimestamp 없는 건 아예 스킵
              .map((m) => ({
                  id: String(m.chatTimestamp!), // ← id를 chatTimestamp로 고정
                  role: m?.role === 'user' || m?.role === 'assistant' ? m.role : 'assistant',
                  text: m?.text ?? '',
                  chatTimestamp: m.chatTimestamp!, // 반드시 숫자
              }))
        : [];

    // 서버 이력 내부에서도 혹시 중복 ts 있으면 정리
    const initialMessagesFromState = dedupByTimestamp(mappedFromServer);

    // ---------- 서버 데이터 항상 최신으로 동기화 ----------
    const STORAGE_KEY = `askToAI:${chatId}`;

    // 페이지 진입 시마다 localStorage 초기화하고 서버 데이터로 시작
    useEffect(() => {
        if (hasChatId) {
            try {
                localStorage.removeItem(STORAGE_KEY);
            } catch {}
        }
    }, [STORAGE_KEY, hasChatId]);

    const initialForHook = initialMessagesFromState;

    // ---------- OpenAI ----------
    const { initializationStatus, isReady, sendMessage } = useOpenAI({
        diagnosisInfo,
        voucher,
        token,
        analysisResult: typeof analysisResult === 'string' ? analysisResult : JSON.stringify(analysisResult),
        testRawData: testRawData || '',
    });

    // ---------- Chat 훅 ----------
    const { messages, addUserMessage, addAssistantMessage } = useChatMessages(initialForHook, chatId);

    // ---------- 렌더용/카운트용으로도 chatTimestamp 중복 제거 ----------
    const renderMessages = dedupByTimestamp(messages);
    const effectiveUserQuestionCount = renderMessages.filter((m: any) => m.role === 'user' && m.countsAsQuestion !== false).length;
    const effectiveRemaining = Math.max(0, effectiveUserQuestionCount);
    const effectiveLimitReached = effectiveUserQuestionCount >= AI_CONSTANTS.MAX_QUESTIONS;

    // ---------- textarea auto-height ----------
    const adjustTextareaHeight = useCallback(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        ta.style.height = '48px';
        ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
    }, []);
    useEffect(() => {
        adjustTextareaHeight();
    }, [input, adjustTextareaHeight]);

    // ---------- header + intersection ----------
    useEffect(() => {
        setHeaderText(
            <HeaderWrapper>
                <HeaderTitleBox>
                    <Body3Medium>ChatGPT에게 질문하기</Body3Medium>
                </HeaderTitleBox>
                <NoticeButton onClick={() => setIsShowHelpModal()}>
                    <Caption1Regular $color={TextColor.color_text_weak}>안내</Caption1Regular>
                </NoticeButton>
            </HeaderWrapper>
        );

        const node = bottomSentinelRef.current;
        if (!node) return;
        const obs = new IntersectionObserver((entries) => setIsAtBottom(entries.some((e) => e.isIntersecting)), { root: scrollerRef.current, threshold: 0.8 });
        obs.observe(node);
        return () => obs.disconnect();
    }, [setHeaderText, setIsShowHelpModal]);

    // ---------- stick to bottom ----------
    const prevLenRef = useRef(0);
    useLayoutEffect(() => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        const len = renderMessages.length;
        if (len > prevLenRef.current && isAtBottom) scroller.scrollTop = scroller.scrollHeight;
        prevLenRef.current = len;
    }, [renderMessages, isAtBottom]);

    // ---------- 페이지 leave 시 localStorage 정리 (React Router 내비게이션 포함) ----------
    useBlocker(({ currentLocation }) => {
        // AskToAi 페이지에서만 실행되도록 제한
        if (currentLocation.pathname.includes('/ask-to-ai') && hasChatId) {
            try {
                localStorage.removeItem(STORAGE_KEY);
            } catch {}
        }
        return false; // 네비게이션을 막지 않고 계속 진행
    });

    useEffect(() => {
        const clear = () => {
            if (hasChatId) {
                try {
                    localStorage.removeItem(STORAGE_KEY);
                } catch {}
            }
        };
        // 브라우저 새로고침/닫기 시에도 정리
        window.addEventListener('beforeunload', clear);
        return () => {
            window.removeEventListener('beforeunload', clear);
            clear();
        };
    }, [STORAGE_KEY, hasChatId]);

    // ---------- send ----------
    const send = useCallback(async () => {
        const text = input.trim();
        if (!text || isLoading || !hasChatId) return;

        const intent = classifyIntent(text);
        const quotaExcluded = isQuotaExcludedIntent(intent);

        // ⬇️ 원래 의도대로: 한도 도달했고 "쿼터 제외가 아닌" 메시지는 막기
        if (effectiveLimitReached && !quotaExcluded) return;

        setInput('');
        setIsLoading(true);

        if (onSendMessage) {
            onSendMessage(text);
            setIsLoading(false);
            return;
        }

        addUserMessage(text, { countsAsQuestion: !quotaExcluded });

        try {
            if (isReady) {
                const response = await sendMessage(text);

                const nextCount = effectiveUserQuestionCount + (quotaExcluded ? 0 : 1);
                const isLastQuestion = nextCount >= AI_CONSTANTS.MAX_QUESTIONS;

                addAssistantMessage(response, isLastQuestion);

                if (!quotaExcluded) {
                    const rqData: AiChatUpdateRqData = {
                        diagnosisId: diagnosisInfo?._id,
                        chatMessageList: [
                            { role: 'user', text, chatTimestamp: Date.now() } as ServerChatMessage,
                            { role: 'assistant', text: response, chatTimestamp: Date.now() + 1 } as ServerChatMessage,
                        ],
                    };
                    await mindUpDiagnosisApi.updateAiChatMessage(rqData, token);
                }
            } else {
                addAssistantMessage(`${MESSAGES.INITIALIZATION_ERROR}\n상태: ${initializationStatus}\n잠시 후 다시 시도해 주세요.`);
            }
        } catch (e) {
            console.error('메시지 전송 실패:', e);
            addAssistantMessage(MESSAGES.RESPONSE_ERROR);
        } finally {
            setIsLoading(false);
        }
    }, [
        input,
        isLoading,
        hasChatId,
        addUserMessage,
        addAssistantMessage,
        isReady,
        sendMessage,
        initializationStatus,
        diagnosisInfo?._id,
        token,
        effectiveUserQuestionCount,
        effectiveLimitReached,
        onSendMessage,
    ]);

    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            send();
        }
    };

    // ---------- render ----------
    return (
        <ChatContainer>
            {!hasChatId && <LoadingOverlay>로딩 중…</LoadingOverlay>}

            {isShowHelpModal && (
                <MobileLeaveModal
                    title='서비스 이용 안내'
                    firstButton='확인'
                    firstButtonType='fill'
                    infoContents={
                        <>
                            {_.map(MODAL_CONTENT.HELP_MODAL.ITEMS, (item, i) => (
                                <AiAskModalContainer key={i}>
                                    <Body3Medium $color={TextColor.color_text_weak}>{item.title}</Body3Medium>
                                    <div style={{ maxWidth: 130 }}>
                                        <Body3Medium>{item.content}</Body3Medium>
                                    </div>
                                </AiAskModalContainer>
                            ))}
                        </>
                    }
                    setIsLeaveModal={setIsShowHelpModal}
                />
            )}

            <MessageList ref={scrollerRef} aria-disabled={!hasChatId}>
                {renderMessages.map((m, idx) => {
                    // user 메시지일 때만 카운트
                    const userIndex = m.role === 'user' ? renderMessages.filter((msg) => msg.role === 'user').findIndex((msg) => msg.chatTimestamp === m.chatTimestamp) + 1 : undefined;

                    return <MessageBubble key={String(m.chatTimestamp ?? idx)} msg={m} userIndex={userIndex} />;
                })}

                {isLoading && (
                    <MessageWrapper $isUser={false}>
                        <MessageSenderContainer>
                            <IcOpenAi />
                            <Caption1Regular>ChatGPT</Caption1Regular>
                        </MessageSenderContainer>
                        <MessageContent $isUser={false}>
                            <LoadingDots>
                                <IcLoadingDots />
                            </LoadingDots>
                        </MessageContent>
                    </MessageWrapper>
                )}

                <div ref={bottomSentinelRef} />
            </MessageList>

            <InputContainer aria-disabled={!hasChatId} $isShow={effectiveRemaining < 3}>
                <QuestionCount>
                    <IcWarningFill />
                    <Body3Medium $color={TextColor.color_text_light}>질문</Body3Medium>
                    <div>
                        <Body3Medium $color={effectiveRemaining < 1 ? TextColor.color_text_weak : PrimaryColor.color_primary_normal}>{effectiveRemaining}</Body3Medium>
                        <Body3Medium $color={TextColor.color_text_light}>/{AI_CONSTANTS.MAX_QUESTIONS}</Body3Medium>
                    </div>
                </QuestionCount>
                <InputWrapper>
                    <TextArea
                        ref={textareaRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={onKeyDown}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        placeholder={!hasChatId ? '로딩 중…' : effectiveLimitReached ? MESSAGES.PLACEHOLDER_LIMIT_REACHED : placeholder}
                        disabled={effectiveLimitReached || !hasChatId}
                    />
                    <SendButton
                        onClick={send}
                        disabled={!hasChatId || !input.trim() || effectiveLimitReached || isLoading}
                        $hasText={!!input.trim() && !effectiveLimitReached && !isLoading && hasChatId}
                        $isFocused={isFocused}
                    >
                        {isFocused || !_.isEmpty(input) ? <IcArrowFillRoundRightBlue /> : <IcArrowFillRoundRightGray />}
                    </SendButton>
                </InputWrapper>
            </InputContainer>

            <InputFinishContainer $isShow={effectiveRemaining > 2}>
                <IcCheckRoundFill />
                <Body4Medium>모든 질문이 끝났어요.</Body4Medium>
            </InputFinishContainer>
        </ChatContainer>
    );
}

function MessageBubble({ msg, userIndex }: { msg: ChatMessage; userIndex?: number }) {
    const isUser = msg.role === 'user';
    return (
        <MessageWrapper $isUser={isUser}>
            {isUser ? (
                <MessageSenderContainer>
                    {/* 날짜 표시 */}
                    <Body5Regular $color={TextColor.color_text_weak}>{formatDate(msg.chatTimestamp)}</Body5Regular>

                    {/* 구분선 */}
                    <ColumnDivider $backgroundcolor={BorderColor.color_border_neutral} $height='10px' $horizontalMargin='10px' />

                    {/* 질문 번호 */}
                    {userIndex && <Body5Medium $color={PrimaryColor.color_primary_normal}>질문 {userIndex}</Body5Medium>}
                </MessageSenderContainer>
            ) : (
                <MessageSenderContainer>
                    <IcOpenAi width='24' height='24' />
                    <Caption1Regular>ChatGPT</Caption1Regular>
                </MessageSenderContainer>
            )}
            <MessageContent $isUser={isUser}>
                <span
                    style={{
                        whiteSpace: 'pre-line',
                        color: isUser ? 'white' : TextColor.color_text_neutral,
                    }}
                >
                    {msg.text}
                </span>
            </MessageContent>
        </MessageWrapper>
    );
}

/* styled-components */
const LoadingOverlay = styled.div`
    position: fixed;
    inset: 56px 0 0 0; /* 헤더 높이 보정 */
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    z-index: 1100;
    font-size: 14px;
    color: ${TextColor.color_text_weak};
`;
const ChatContainer = styled.div`
    width: 100%;
    height: calc(100dvh - 56px);
    display: flex;
    flex-direction: column;
    background-color: white;
    margin-top: -56px;
`;

const MessageList = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 30px 12px 120px 12px;
    background-color: white;
`;

const InputFinishContainer = styled.div<{ $isShow?: boolean }>`
    display: ${(props) => (props.$isShow ? 'flex' : 'none')};
    align-items: center;
    gap: 4px;
    position: fixed;
    bottom: 0px;
    left: 50%;
    transform: translateX(-50%);
    background-color: white;
    padding: 12px 16px;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    width: 100%;
    z-index: 4;
    background-color: ${BackgroundColor.color_background_neutral};
`;

const InputContainer = styled.div<{ $isShow?: boolean }>`
    display: ${(props) => (props.$isShow ? 'flex' : 'none')};
    flex-direction: column;
    gap: 6px;
    position: fixed;
    bottom: 0px;
    left: 50%;
    transform: translateX(-50%);
    background-color: white;
    padding: 12px 13px 12px 16px;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    width: 100%;
    z-index: 4;
    background-color: ${BackgroundColor.color_background_neutral};
`;

const QuestionCount = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const InputWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: flex-start;
    justify-content: center;
`;

const TextArea = styled.textarea`
    width: 100%;
    min-height: 48px;
    max-height: 120px;
    height: 48px;
    outline: none;
    font-size: 16px;
    line-height: 1.5;
    border-radius: 8px;
    border: 1px solid #d1d5db;
    padding: 12px 50px 12px 12px;
    resize: none;
    overflow: hidden;

    &:focus {
        outline: none;
        border: 1px solid #d1d5db;
    }

    &:disabled {
        background-color: #f3f4f6;
        color: #9ca3af;
        cursor: not-allowed;
    }
`;

const SendButton = styled.button<{ $hasText: boolean; $isFocused: boolean }>`
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: transparent;

    color: ${(props) => {
        if (!props.$hasText && !props.$isFocused) return '#d1d5db';
        if (props.$isFocused || props.$hasText) return '#3b82f6';
        return '#d1d5db';
    }};

    cursor: ${(props) => (props.$hasText ? 'pointer' : 'default')};

    &:hover {
        background-color: ${(props) => (props.$hasText ? '#f3f4f6' : 'transparent')};
    }

    &:disabled {
        cursor: not-allowed;
    }
`;

const MessageWrapper = styled.div<{ $isUser: boolean }>`
    margin: 8px 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: ${(props) => (props.$isUser ? 'end' : 'start')};
`;

const MessageSenderContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 2;
`;

const MessageContent = styled.div<{ $isUser: boolean }>`
    max-width: 80%;
    border-radius: 16px;
    padding: 8px 12px;
    background-color: ${(props) => (props.$isUser ? PrimaryColor.color_primary_normal : BackgroundColor.color_background_primary_normal)};
    color: ${(props) => (props.$isUser ? 'white' : 'black')};
`;

const HeaderWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-left: 63px;
    gap: 4px;
`;

const HeaderTitleBox = styled.div`
    position: absolute;
    display: flex;
    left: 50%;
    transform: translateX(-50%);
    justify-content: center;
    align-items: start;
`;

const NoticeButton = styled.div`
    position: absolute;
    right: 16px;
    padding: 6px 12px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid ${BorderColor.color_border_light};
    border-radius: 999px;
`;

const LoadingDots = styled.div`
    display: flex;
    align-items: center;
    padding: 14px 16px;
    animation: pulse 1.5s infinite;

    @keyframes pulse {
        0%,
        100% {
            opacity: 1;
        }
        50% {
            opacity: 0.5;
        }
    }
`;

const AiAskModalContainer = styled.div<{ $gap?: string }>`
    display: flex;
    padding: 4px 0px;
    align-items: start;
    gap: ${({ $gap }) => $gap || '20px'};
`;
